import { useEffect, useState } from 'react';
import { fetchRateCard, type MarginView, type RateCardFluidSpecRow } from '../bidService';
import { getBidUuid } from '../bidStore';
import { money, useFinancialVisible } from '../financialDisplay';
import {
  FLUID_LINE_PREFIX,
  findLineByPrefix,
  fluidVolumeM3,
  newId,
  type BidLineItem,
  type BidPhase,
} from './bidWorkbook';

/**
 * Hydro-test fluid rental calculator (gap 4.1 — the formula confirmed 2026-08-07):
 *
 *   volume (m³) = length × pipe-spec m³/m × fill factor
 *   charge      = volume × fluidRate ($/m³) over fluidDays  — i.e. qty × rate × days,
 *
 * which is exactly the engine's own line algebra, so the calculator derives COMPONENTS and
 * applies them as one visible line the estimator confirms (the endorsed 4.1 frame) — the
 * server prices it like any other line, and a later default change never reprices it (the
 * rate freezes at Apply, the workbook's own doctrine).
 *
 * Sources, all frozen or resolved — the calculator re-implements nothing:
 *  * pipe specs come from the bid's FROZEN rate-card snapshot, never the live catalog;
 *  * fluidRate / fluidDays / fillFactor resolve per-bid-override ?? company-default through
 *    the margin machinery (edit them in the Margins panel, not here);
 *  * length seeds from the bid's ROW length and stays editable (a test section can be shorter).
 *
 * Wholly a money surface, so a blind caller gets nothing (the MarginPanel idiom) — unlike the
 * loads card, whose count matrix is quantities. A missing ingredient refuses the draft and
 * names itself; nothing here mints a $0 line out of absence.
 */
interface FluidRentalCardProps {
  bidId: string;
  phases: BidPhase[];
  rowLengthM: number;
  /** Resolved margin view (fetched by the summary view; null while loading or blind). */
  margins: MarginView | null;
  /** Status edit-lock: Apply is withheld — the workbook write it feeds is 409-refused anyway. */
  locked?: boolean;
  onApplyLine: (phaseId: string, line: BidLineItem, replaceItemId?: string) => void;
}

const parseParam = (v: string | null | undefined): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const FluidRentalCard = ({
  bidId, phases, rowLengthM, margins, locked = false, onApplyLine,
}: FluidRentalCardProps): JSX.Element | null => {
  const financialVisible = useFinancialVisible();
  const uuid = getBidUuid(bidId);

  const [specs, setSpecs] = useState<RateCardFluidSpecRow[] | null>(null);
  const [cardMissing, setCardMissing] = useState(false);
  const [designation, setDesignation] = useState('');
  const [lengthDraft, setLengthDraft] = useState('');
  const [lengthTouched, setLengthTouched] = useState(false);
  const [targetPhaseId, setTargetPhaseId] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!uuid || !financialVisible) return undefined;
    void fetchRateCard(uuid)
      .then((card) => {
        if (cancelled) return;
        // null = no frozen card (a pre-snapshot bid). Disclosed below, NOT silently substituted
        // with the live catalog — there is no frozen truth to price this bid's fluid from.
        if (!card) { setCardMissing(true); return; }
        setSpecs(card.fluidSpecs);
      })
      // Transient failure -> the card just never appears (the MarginPanel idiom); a decorative
      // pricing surface earns no retry panel, and the server stays the boundary.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uuid, financialVisible]);

  // Length follows ROW length until the estimator types their own (the touched-flag guard) —
  // a hand-entered test length is never overwritten by a later ROW edit.
  useEffect(() => {
    if (!lengthTouched) setLengthDraft(rowLengthM > 0 ? String(rowLengthM) : '');
  }, [rowLengthM, lengthTouched]);

  // Money surface: the server refuses margins/rate-card to a blind caller — nothing to show.
  if (!financialVisible || margins === null) return null;
  if (!cardMissing && specs === null) return null; // still loading — same as money starting redacted

  const eff = margins.resolved?.effective ?? {};
  const fluidRate = parseParam(eff.fluid_rate);
  const fluidDays = parseParam(eff.fluid_days);
  // Rates and days honour configured-0 (free fluid / no rental days are statements a company
  // can make); a FACTOR of 0 is not — it zeroes every volume, so ≤0 counts as unusable and
  // refuses by name below rather than leaving a silently dead draft (review).
  const fillRaw = parseParam(eff.fill_factor);
  const fillFactor = fillRaw !== null && fillRaw > 0 ? fillRaw : null;
  const missingParams = [
    fluidRate === null ? 'Fluid $/m³' : null,
    fluidDays === null ? 'Fluid days' : null,
    fillFactor === null ? 'a usable Fill factor' : null,
  ].filter(Boolean) as string[];

  const spec = (specs ?? []).find((s) => s.designation === designation) ?? null;
  const lengthM = parseFloat(lengthDraft);
  const lengthOk = Number.isFinite(lengthM) && lengthM > 0;

  const volume = spec && spec.m3PerM !== null && lengthOk && fillFactor !== null
    ? fluidVolumeM3(lengthM, spec.m3PerM, fillFactor)
    : 0;
  const ready = volume > 0 && fluidRate !== null && fluidDays !== null;
  const charge = ready ? volume * (fluidRate as number) * (fluidDays as number) : 0;

  const applied = findLineByPrefix(phases, FLUID_LINE_PREFIX);
  const defaultPhaseId = (phases.find((p) => /pig|test|fluid/i.test(p.name)) ?? phases[0])?.id ?? '';
  const effectiveTarget = targetPhaseId || defaultPhaseId;

  const apply = (): void => {
    if (!ready) return;
    // Provenance in words, never money (the description survives the financial strip).
    const description = `${FLUID_LINE_PREFIX}${designation}, ${lengthM} m, fill ${fillFactor}`
      .slice(0, 255);
    const parts = {
      description, qty: volume, hrs: 1, days: fluidDays as number, rate: fluidRate as number,
    };
    if (applied) {
      onApplyLine(applied.phase.id, { ...applied.item, ...parts }, applied.item.id);
      return;
    }
    if (!effectiveTarget) return;
    onApplyLine(effectiveTarget, {
      id: newId('eq'), kind: 'equipment', source: 'inhouse', ...parts,
    });
  };

  return (
    <div className="bid-cover bid-fluid-card">
      <h2 className="bid-panel-title">Hydro-test Fluid</h2>
      <p className="bid-panel-subtitle">
        Volume = length × pipe-spec m³/m × fill factor; charged at the fluid rate over the
        rental days. Applies as one visible line.
      </p>
      {cardMissing ? (
        <p className="bid-calc-pricing-note">
          This bid has no frozen rate card, so its pipe specs are unavailable — fluid pricing
          needs one.
        </p>
      ) : (specs ?? []).length === 0 ? (
        <p className="bid-calc-pricing-note">
          This bid’s frozen rate card holds no pipe specs (it predates the fluid table) —
          fluid pricing is unavailable for it.
        </p>
      ) : (
        <>
          <div className="bid-fluid-grid">
            <label className="bid-field">
              <span className="bid-field-label">Pipe spec</span>
              <select
                className="bid-input"
                aria-label="Pipe spec"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
              >
                <option value="">Select pipe spec…</option>
                {(specs ?? []).map((s) => (
                  <option key={s.designation} value={s.designation}>{s.designation}</option>
                ))}
              </select>
            </label>
            <label className="bid-field">
              <span className="bid-field-label">Length (m)</span>
              <input
                type="number"
                min={0}
                className="bid-input"
                aria-label="Fluid length"
                value={lengthDraft}
                onChange={(e) => { setLengthTouched(true); setLengthDraft(e.target.value); }}
              />
            </label>
            <div className="bid-field">
              <span className="bid-field-label">m³/m</span>
              <div className="bid-derived">{spec?.m3PerM ?? '—'}</div>
            </div>
            <div className="bid-field">
              <span className="bid-field-label">Fill factor</span>
              <div className="bid-derived">{fillFactor ?? '—'}</div>
            </div>
            <div className="bid-field">
              <span className="bid-field-label">Volume</span>
              <div className="bid-derived">{volume > 0 ? `${volume} m³` : '—'}</div>
            </div>
            <div className="bid-field">
              <span className="bid-field-label">Rate × days</span>
              <div className="bid-derived">
                {fluidRate !== null && fluidDays !== null
                  ? `${money(fluidRate)}/m³ × ${fluidDays} d`
                  : '—'}
              </div>
            </div>
          </div>
          {missingParams.length > 0 && (
            <p className="bid-calc-pricing-note">
              Fluid pricing needs {missingParams.join(', ')} — set them in Margins below, or in
              the company defaults.
            </p>
          )}
          {spec !== null && (spec.m3PerM === null || spec.m3PerM <= 0) && (
            <p className="bid-calc-pricing-note">
              The frozen spec for “{spec.designation}” carries no usable m³/m figure — a volume
              cannot be derived from it.
            </p>
          )}
          {designation !== '' && !lengthOk && (
            <p className="bid-calc-pricing-note">Enter a pipeline length in metres.</p>
          )}
          {ready && (
            <>
              <p className="bid-calc-pricing-draft">
                {volume} m³ × {money(fluidRate)} / m³ × {fluidDays} days ={' '}
                <strong>{money(charge)}</strong>
              </p>
              {locked ? (
                <p className="bid-calc-pricing-note">
                  Pricing is locked while the bid is a record — the applied line, if any, stays
                  as submitted.
                </p>
              ) : phases.length === 0 ? (
                <p className="bid-calc-pricing-note">
                  Add a phase first — the priced line lives in a phase.
                </p>
              ) : (
                <div className="bid-calc-pricing-actions">
                  {applied ? (
                    <span className="bid-calc-pricing-note">
                      Applied in “{applied.phase.name || 'Untitled phase'}”. Renaming the line
                      hands it over to hand editing.
                    </span>
                  ) : (
                    <select
                      className="bid-input"
                      aria-label="Phase for the fluid line"
                      value={effectiveTarget}
                      onChange={(e) => setTargetPhaseId(e.target.value)}
                    >
                      {phases.map((p) => (
                        <option key={p.id} value={p.id}>{p.name || 'Untitled phase'}</option>
                      ))}
                    </select>
                  )}
                  <button className="bid-add-row" onClick={apply}>
                    {applied ? 'Update applied line' : 'Apply as line'}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default FluidRentalCard;
